import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PermissionWrapper } from "../../PermissionWrapper/PermissionWrapper";

import "./ActionMenu.css";

// Module-level registry so any menu can close all others when it opens.
const _menuClosers = new Set<() => void>();

export type AdsActionMenuItem = {
  key: string;
  label: string;
  onSelect: () => void;
  /** Optional icon className (e.g. `ph-eye`) */
  iconClassName?: string;
  /** Permission code for this action (e.g. `ads.view`) */
  permission?: string;
  /** Alternative: require any of these permissions */
  anyOfPermissions?: string[];
  /** Hide item when user does not have permission. Defaults to true. */
  hideWhenDisabled?: boolean;
  /** Mark as destructive */
  isDanger?: boolean;
  /** Visually disable this item — it remains visible but unclickable */
  disabled?: boolean;
};

export interface ActionMenuProps {
  items: AdsActionMenuItem[];
  ariaLabel?: string;
  /** For compact tables, keep the 3-dot within the last column. */
  size?: "sm" | "md";
}

export const ActionMenu: React.FC<ActionMenuProps> = ({ items, ariaLabel = "Actions", size = "md" }) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

  const computePosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const dropdownWidth = 170;
    const dropdownHeight = Math.min(220, Math.max(60, items.length * 42));

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    let xPosition = rect.right - dropdownWidth;
    if (xPosition < 8) xPosition = rect.left;
    if (xPosition + dropdownWidth > viewportWidth - 8) xPosition = viewportWidth - dropdownWidth - 8;

    let yPosition: number;
    if (spaceBelow >= dropdownHeight) {
      yPosition = rect.bottom + 4;
    } else if (spaceAbove >= dropdownHeight) {
      yPosition = rect.top - dropdownHeight - 4;
    } else {
      const maxY = Math.max(8, viewportHeight - dropdownHeight - 8);
      yPosition = Math.min(rect.bottom + 4, maxY);
    }

    setPosition({ x: xPosition, y: yPosition });
  }, [items.length]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setPosition(null);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(closeMenu, 120);
  }, [cancelClose, closeMenu]);

  // Clean up any pending timer on unmount.
  useEffect(() => () => { cancelClose(); }, [cancelClose]);

  // Register / deregister this instance so other menus can close it.
  useEffect(() => {
    _menuClosers.add(closeMenu);
    return () => { _menuClosers.delete(closeMenu); };
  }, [closeMenu]);

  const openMenu = useCallback(() => {
    cancelClose();
    // Close every other open menu before opening this one.
    _menuClosers.forEach((close) => { if (close !== closeMenu) close(); });
    computePosition();
    setOpen(true);
  }, [computePosition, closeMenu, cancelClose]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDownOutside = (event: MouseEvent) => {
      const dropdown = dropdownRef.current;
      const button = buttonRef.current;
      const target = event.target as Node | null;
      if (!target) return;

      if (dropdown && dropdown.contains(target)) return;
      if (button && button.contains(target)) return;

      closeMenu();
    };

    document.addEventListener("mousedown", handleMouseDownOutside);
    return () => document.removeEventListener("mousedown", handleMouseDownOutside);
  }, [open, closeMenu]);

  const buttonClassName = useMemo(() => {
    const base = "ads-action-menu__button";
    return size === "sm" ? `${base} ads-action-menu__button--sm` : `${base} ads-action-menu__button--md`;
  }, [size]);

  return (
    <div className="ads-action-menu">
      <button
        ref={buttonRef}
        type="button"
        className={buttonClassName}
        aria-label={ariaLabel}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
      >
        <i className="ph-dots-three-vertical" aria-hidden="true" />
      </button>

      {open && position && createPortal(
        <div
          ref={dropdownRef}
          className="ads-action-menu__dropdown"
          style={{ top: position.y, left: position.x }}
          onMouseEnter={cancelClose}
          onMouseLeave={closeMenu}
        >
          {items.map((item) => {
            const content = (
              <div
                key={item.key}
                className={[
                  "ads-action-menu__item",
                  item.isDanger && !item.disabled ? "ads-action-menu__item--danger" : "",
                  item.disabled ? "ads-action-menu__item--disabled" : "",
                ].filter(Boolean).join(" ")}
                role="menuitem"
                tabIndex={item.disabled ? -1 : 0}
                aria-disabled={item.disabled || undefined}
                onClick={item.disabled ? undefined : () => {
                  item.onSelect();
                  closeMenu();
                }}
                onKeyDown={item.disabled ? undefined : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    item.onSelect();
                    closeMenu();
                  }
                }}
              >
                {item.iconClassName && <i className={item.iconClassName} aria-hidden="true" />}
                <span>{item.label}</span>
              </div>
            );

            const hasPermissions = Boolean(item.permission || (item.anyOfPermissions && item.anyOfPermissions.length > 0));
            if (!hasPermissions) return content;

            return (
              <PermissionWrapper
                key={item.key}
                permission={item.permission}
                anyOfPermissions={item.anyOfPermissions}
                hideWhenDisabled={item.hideWhenDisabled ?? true}
              >
                {content}
              </PermissionWrapper>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

